import type { RouteLocationRaw, RouteParams, RouteParamsRawGeneric } from "vue-router";

import { Schulform } from "@core/asd/types/schule/Schulform";
import { DeveloperNotificationException } from "@core/core/exceptions/DeveloperNotificationException";
import { BenutzerKompetenz } from "@core/core/types/benutzer/BenutzerKompetenz";
import { ServerMode } from "@core/core/types/ServerMode";
import { AppMenuManager } from "@ui/ui/nav/AppMenuManager";
import type { TabData } from "@ui/ui/nav/TabData";
import type { TabManager } from "@ui/ui/nav/TabManager";
import { ViewType } from "@ui/ui/nav/ViewType";

import { routeError } from "../error/RouteError";
import SApp from "~/components/SApp.vue";
import type { AppProps } from "~/components/SAppProps";
import { api } from "~/router/Api";
import { routeBenutzerprofilNutzereinstellungen } from "~/router/apps/benutzerprofil/nutzereinstellungen/RouteBenutzerprofilNutzereinstellungen";
import { routeBenutzerprofilWiedervorlagen } from "~/router/apps/benutzerprofil/wiedervorlagen/RouteBenutzerprofilWiedervorlagen";
import { routeEinstellungenBenutzer } from "~/router/apps/einstellungen/benutzer/RouteEinstellungenBenutzer";
import { routeEinstellungenBenutzergruppe } from "~/router/apps/einstellungen/benutzergruppen/RouteEinstellungenBenutzergruppe";
import { routeEmailServer } from "~/router/apps/einstellungen/emailserver/RouteEmailServer";
import { routeGost } from "~/router/apps/gost/RouteGost";
import { routeKlassen } from "~/router/apps/klassen/RouteKlassen";
import { routeKurse } from "~/router/apps/kurse/RouteKurse";
import { routeLehrer } from "~/router/apps/lehrer/RouteLehrer";
import { RouteDataApp } from "~/router/apps/RouteDataApp";
import { routeSchueler } from "~/router/apps/schueler/RouteSchueler";
import { routeSchuleDatenaustauschLernplattformen } from "~/router/apps/schule/datenaustausch/lernplattformenExport/RouteSchuleDatenaustauschLernplattformen";
import { routeAbteilungen } from "~/router/apps/schule/kataloge/abteilungen/RouteAbteilungen";
import { routeAnkreuzkompetenzen } from "~/router/apps/schule/kataloge/ankreuzkompetenzen/RouteAnkreuzkompetenzen";
import { routeBeschaeftigungsarten } from "~/router/apps/schule/kataloge/beschaeftigungsarten/RouteBeschaeftigungsarten";
import { routeBetriebe } from "~/router/apps/schule/kataloge/betriebe/RouteBetriebe";
import { routeEinwilligungsarten } from "~/router/apps/schule/kataloge/einwilligungsarten/RouteEinwilligungsarten";
import { routeEntlassgruende } from "~/router/apps/schule/kataloge/entlassgruende/RouteEntlassgruende";
import { routeErzieherarten } from "~/router/apps/schule/kataloge/erzieherarten/RouteErzieherarten";
import { routeFachklassen } from "~/router/apps/schule/kataloge/fachklassen/RouteFachklassen";
import { routeFaecher } from "~/router/apps/schule/kataloge/faecher/RouteFaecher";
import { routeFahrschuelerarten } from "~/router/apps/schule/kataloge/fahrschuelerarten/RouteFahrschuelerarten";
import { routeFloskelgruppen } from "~/router/apps/schule/kataloge/floskelgruppen/RouteFloskelgruppen";
import { routeFloskeln } from "~/router/apps/schule/kataloge/floskeln/RouteFloskeln";
import { routeFoerderschwerpunkte } from "~/router/apps/schule/kataloge/foerderschwerpunkte/RouteFoerderschwerpunkte";
import { routeHaltestellen } from "~/router/apps/schule/kataloge/haltestellen/RouteHaltestellen";
import { routeJahrgaenge } from "~/router/apps/schule/kataloge/jahrgaenge/RouteJahrgaenge";
import { routeKindergaerten } from "~/router/apps/schule/kataloge/kindergaerten/RouteKindergaerten";
import { routeKonfessionen } from "~/router/apps/schule/kataloge/konfessionen/RouteKonfessionen";
import { routeLernplattformen } from "~/router/apps/schule/kataloge/lernplattformen/RouteLernplattformen";
import { routeOrte } from "~/router/apps/schule/kataloge/orte/RouteOrte";
import { routeOrtsteile } from "~/router/apps/schule/kataloge/ortsteile/RouteOrtsteile";
import { routeSchulen } from "~/router/apps/schule/kataloge/schulen/RouteSchulen";
import { routeSchulinterneFunktionen } from "~/router/apps/schule/kataloge/schulinternefunktionen/RouteSchulinterneFunktionen";
import { routeSchwerpunkte } from "~/router/apps/schule/kataloge/schwerpunkte/RouteSchwerpunkte";
import { routeTeilleistungsarten } from "~/router/apps/schule/kataloge/teilleistungsarten/RouteTeilleistungsarten";
import { routeTelefonarten } from "~/router/apps/schule/kataloge/telefonarten/RouteTelefonarten";
import { routeVermerkarten } from "~/router/apps/schule/kataloge/vermerkarten/RouteVermerkarten";
import { routeSchule } from "~/router/apps/schule/RouteSchule";
import { routeSchuleStammdaten } from "~/router/apps/schule/stammdaten/RouteSchuleStammdaten";
import { routeStatistik } from "~/router/apps/statistik/RouteStatistik";
import { routeStundenplan } from "~/router/apps/stundenplan/RouteStundenplan";
import { routeUv } from "~/router/apps/unterrichtsverteilung/RouteUv";
import { routeLogin } from "~/router/login/RouteLogin";
import { RouteManager } from "~/router/RouteManager";
import { RouteNode } from "~/router/RouteNode";
import { RoutingStatus } from "~/router/RoutingStatus";
import { abschnittStateImpl } from "~/states/AbschnittStateImpl";
import { benutzerStateImpl } from "~/states/BenutzerStateImpl";
import { serverStateImpl } from "~/states/ServerStateImpl";
import { wiedervorlageStateImpl } from "~/states/wiedervorlage/WiedervorlageStateImpl";

import { routeBenutzerprofil } from "./benutzerprofil/RouteBenutzerprofil";
import { routeEinstellungen } from "./einstellungen/RouteEinstellungen";
import { routeNotenmodul } from "./notenmodul/RouteNotenmodul";
import { routeNotenmodulAdministration } from "./notenmodul/RouteNotenmodulAdministration";
import { routeNotenmodulAnkreuzkompetenzen } from "./notenmodul/RouteNotenmodulAnkreuzkompetenzen";
import { routeNotenmodulKlassenleitung } from "./notenmodul/RouteNotenmodulKlassenleitung";
import { routeNotenmodulLeistungen } from "./notenmodul/RouteNotenmodulLeistungen";
import { routeNotenmodulTeilleistungen } from "./notenmodul/RouteNotenmodulTeilleistungen";
import { routeNotenmodulZugangsdaten } from "./notenmodul/RouteNotenmodulZugangsdaten";
import { routeSchuleDatenaustauschENM } from "./schule/datenaustausch/enmNotenmanager/RouteSchuleDatenaustauschENM";
import { routeSchuleDatenaustauschKurs42 } from "./schule/datenaustausch/kurs42/RouteSchuleDatenaustauschKurs42";
import { routeSchuleDatenaustauschLaufbahnplanung } from "./schule/datenaustausch/laufbahnplanung/RouteSchuleDatenaustauschLupo";
import { routeSchulwechsel } from "./schule/datenaustausch/schulwechsel/RouteSchulwechsel";
import { routeSchuleDatenaustauschUntis } from "./schule/datenaustausch/untis/RouteSchuleDatenaustauschUntis";
import { routeBetriebsarten } from "./schule/kataloge/betriebsarten/RouteBetriebsarten";
import { routeSchuleReporting } from "./schule/reporting/RouteSchuleReporting";
import { routeSchuleReportingKonfiguration } from "./schule/reporting/RouteSchuleReportingKonfiguration";


export class RouteApp extends RouteNode<RouteDataApp, any> {

	/** Die Knoten, welche im Haupt-Menu zur Verfügung gestellt werden */
	private readonly _menuMain: RouteNode<any, any>[];

	public menuHidden(): boolean[] {
		return super.menu.map(c => c.hidden(RouteManager.instance.getRouteParams()) !== false);
	}

	/** Die Knoten, welche im Menu Einstellungen zur Verfügung gestellt werden */
	// TODO in abstrahierter Form in RouteNode integrieren...
	private readonly _menuEinstellungen: RouteNode<any, any>[];
	public get menuEinstellungen(): RouteNode<any, any>[] {
		const result: RouteNode<any, any>[] = [];
		for (const node of this._menuEinstellungen) {
			if (benutzerStateImpl.authenticated && (!node.mode.checkServerMode(serverStateImpl.mode) || !node.hatSchulform() || !node.hatEineKompetenz())) {
				continue;
			}
			result.push(node);
		}
		return result;
	}
	public menuEinstellungenHidden(): boolean[] {
		return this.menuEinstellungen.map(c => c.hidden(RouteManager.instance.getRouteParams()) !== false);
	}

	/** Die Knoten, welche im Menu Benutzerprofil zur Verfügung gestellt werden */
	// TODO in abstrahierter Form in RouteNode integrieren...
	private readonly _menuBenutzerprofil: RouteNode<any, any>[];
	public get menuBenutzerprofil(): RouteNode<any, any>[] {
		const result: RouteNode<any, any>[] = [];
		for (const node of this._menuBenutzerprofil) {
			if (benutzerStateImpl.authenticated && (!node.mode.checkServerMode(serverStateImpl.mode) || !node.hatSchulform() || !node.hatEineKompetenz())) {
				continue;
			}
			result.push(node);
		}
		return result;
	}
	public menuBenutzerprofilHidden(): boolean[] {
		return this.menuBenutzerprofil.map(c => c.hidden(RouteManager.instance.getRouteParams()) !== false);
	}

	/** Die Knoten, welche im Menu Schule zur Verfügung gestellt werden */
	// TODO in abstrahierter Form in RouteNode integrieren...
	private readonly _menuSchule: RouteNode<any, any>[];
	public get menuSchule(): RouteNode<any, any>[] {
		const result: RouteNode<any, any>[] = [];
		for (const node of this._menuSchule) {
			if (benutzerStateImpl.authenticated && (!node.mode.checkServerMode(serverStateImpl.mode) || !node.hatSchulform() || !node.hatEineKompetenz())) {
				continue;
			}
			result.push(node);
		}
		return result;
	}
	public menuSchuleHidden(): boolean[] {
		return this.menuSchule.map(c => c.hidden(RouteManager.instance.getRouteParams()) !== false);
	}

	/** Die Knoten, welche im Menu Notenmodul zur Verfügung gestellt werden */
	// TODO in abstrahierter Form in RouteNode integrieren...
	private readonly _menuNotenmodul: RouteNode<any, any>[];
	public get menuNotenmodul(): RouteNode<any, any>[] {
		const result: RouteNode<any, any>[] = [];
		for (const node of this._menuNotenmodul) {
			if (benutzerStateImpl.authenticated && (!node.mode.checkServerMode(serverStateImpl.mode) || !node.hatSchulform() || !node.hatEineKompetenz())) {
				continue;
			}
			result.push(node);
		}
		return result;
	}
	public menuNotenmodulHidden(): boolean[] {
		return this.menuNotenmodul.map(c => c.hidden(RouteManager.instance.getRouteParams()) !== false);
	}

	public constructor() {
		super(Schulform.values(), [BenutzerKompetenz.KEINE], "app", String.raw`/:schema?/:idSchuljahresabschnitt(\d+)?`, SApp, new RouteDataApp());
		super.mode = ServerMode.STABLE;
		super.propHandler = (route) => this.getProps();
		super.text = "SVWS-Client";
		this._menuMain = [
			routeBenutzerprofil,
			routeSchule,
			routeSchueler,
			routeLehrer,
			routeKlassen,
			routeKurse,
			routeNotenmodul,
			routeGost,
			routeStatistik,
			routeStundenplan,
			routeUv,
			routeEinstellungen,
		];
		this._menuBenutzerprofil = [
			// Aufgaben
			routeBenutzerprofilWiedervorlagen,
			// Einstellungen
			routeBenutzerprofilNutzereinstellungen,
		];
		this._menuEinstellungen = [
			routeEmailServer,
			routeEinstellungenBenutzer,
			routeEinstellungenBenutzergruppe,
		];
		this._menuSchule = [
			// Stammdaten
			routeSchuleStammdaten,
			// Kataloge
			routeAbteilungen,
			routeAnkreuzkompetenzen,
			routeBetriebe,
			routeBeschaeftigungsarten,
			routeBetriebsarten,
			routeEinwilligungsarten,
			routeEntlassgruende,
			routeErzieherarten,
			routeFachklassen,
			routeFaecher,
			routeFahrschuelerarten,
			routeFloskelgruppen,
			routeFloskeln,
			routeFoerderschwerpunkte,
			routeHaltestellen,
			routeJahrgaenge,
			routeKindergaerten,
			routeKonfessionen,
			routeLernplattformen,
			routeOrte,
			routeOrtsteile,
			routeSchulen,
			routeSchulinterneFunktionen,
			routeSchwerpunkte,
			routeTeilleistungsarten,
			routeTelefonarten,
			routeVermerkarten,
			// Datenaustausch
			routeSchuleDatenaustauschENM,
			routeSchulwechsel,
			routeSchuleDatenaustauschLaufbahnplanung,
			routeSchuleDatenaustauschKurs42,
			routeSchuleDatenaustauschUntis,
			routeSchuleDatenaustauschLernplattformen,
			// Reporting
			routeSchuleReporting,
			routeSchuleReportingKonfiguration,
		];
		this._menuNotenmodul = [
			routeNotenmodulLeistungen,
			routeNotenmodulTeilleistungen,
			routeNotenmodulAnkreuzkompetenzen,
			routeNotenmodulKlassenleitung,
			routeNotenmodulAdministration,
			routeNotenmodulZugangsdaten,
		];
		super.children = [
			...this._menuBenutzerprofil,
			...this._menuMain,
			...this._menuSchule,
			...this._menuNotenmodul,
			...this._menuEinstellungen,
		];
		super.menu = this._menuMain;
		super.defaultChild = routeSchueler;
	}

	public async update(to: RouteNode<any, any>, to_params: RouteParams, from: RouteNode<any, any> | undefined, from_params: RouteParams, isEntering: boolean): Promise<void | Error | RouteLocationRaw> {
		// if route is a parent route node
		if (to.children.length > 0) {
			await wiedervorlageStateImpl.updateAnzahlOffeneWiedervorlagen();
		}

		try {
			const { idSchuljahresabschnitt } = RouteNode.getIntParams(to_params, ["idSchuljahresabschnitt"]);
			// Prüfe, ob der Schuljahresabschnitt gültig gesetzt ist
			if (idSchuljahresabschnitt === undefined) {
				return this.getRouteDefaultChild({ idSchuljahresabschnitt: abschnittStateImpl.auswahl.id });
			}
			// Prüfe, ob der Schuljahresabschnitt gesetzt werden soll
			await abschnittStateImpl.setAuswahl(idSchuljahresabschnitt);
			// Prüfe, ob die View aktualisiert werden muss
			let cur: RouteNode<any, any> = to;
			while (cur.parent !== this) {
				cur = cur.parent;
			}
			if (cur !== this.data.view) {
				this.data.setView(cur, this.children);
			}
		} catch (e) {
			return await routeError.getErrorRoute(e as DeveloperNotificationException);
		}
	}

	public async leave(from: RouteNode<any, any>, from_params: RouteParams): Promise<void> {
		await this.data.leave();
	}

	public addRouteParamsFromState(): RouteParamsRawGeneric {
		const schema = encodeURIComponent(api.schema);
		const idSchuljahresabschnitt = abschnittStateImpl.auswahl.id;
		return { schema, idSchuljahresabschnitt };
	}

	public getProps(): AppProps {
		return {
			logout: routeLogin.logout,
			schemaname: api.schema,
			// Props für die Navigation
			menu: this.getMenuManager(),
			benutzerprofilApp: { name: routeBenutzerprofil.name, text: routeBenutzerprofil.text, hide: true },
			apiStatus: api.status,
			tabManagerSchule: this.getTabManagerSchule,
			tabManagerBenutzerprofil: this.getTabManagerBenutzerprofil,
			tabManagerEinstellungen: this.getTabManagerEinstellungen,
			tabManagerNotenmodul: this.getTabManagerNotenmodul,
		};
	}

	private getSelectedChild(): TabData {
		const child = this.selectedChild!;
		return { name: child.name, text: child.text, hide: false };
	}

	private getApp(): TabData {
		return { name: (this.data.view.name === "stundenplan.kataloge") ? "stundenplan" : this.data.view.name, text: this.data.view.text, hide: !this.data.view.hasView('liste') };
	}

	private getApps(): TabData[] {
		const result: TabData[] = [];
		for (const c of super.menu) {
			if (c.hatEineKompetenz() && c.hatSchulform()) {
				result.push({ name: c.name, text: c.text });
			}
		}
		return result;
	}

	private readonly setApp = async (value: TabData) => {
		if (value.name === this.data.view.name) {
			return;
		}
		let node = RouteNode.getNodeByName(value.name);
		if (node === undefined) {
			throw new DeveloperNotificationException("Unbekannte Route");
		}
		if (node === routeEinstellungen) {
			node = this.menuEinstellungen.at(0);
		} else	if (node === routeBenutzerprofil) {
			node = this.menuBenutzerprofil.at(0);
		} else if (node === routeSchule) {
			node = this.menuSchule.at(0);
		} else if (node === routeNotenmodul) {
			node = this.menuNotenmodul.at(0);
		}
		if (node === undefined) {
			return;
		}
		const result = await RouteManager.doRoute(node.getRoute());
		if (result === RoutingStatus.SUCCESS) {
			this.data.setView(node, this.children);
		}
	};

	private getMenuManager(): AppMenuManager {
		const submenuManager = new Array<{ name: string, manager: TabManager }>();
		if (routeBenutzerprofil.hidden() === false) {
			submenuManager.push({ name: "benutzerprofil", manager: this.getTabManagerBenutzerprofil() });
		}
		if (routeSchule.hidden() === false) {
			submenuManager.push({ name: "schule", manager: this.getTabManagerSchule() });
		}
		if (routeNotenmodul.hidden() === false) {
			submenuManager.push({ name: "notenmodul", manager: this.getTabManagerNotenmodul() });
		}
		if (routeEinstellungen.hidden() === false) {
			submenuManager.push({ name: "einstellungen", manager: this.getTabManagerEinstellungen() });
		}
		return new AppMenuManager(this.getTabManager(), submenuManager, this.getApp());
	}

	private getTabManager(): TabManager {
		return this.createTabManager(super.menu, this.menuHidden(), this.data.view.name, this.setApp, ViewType.DEFAULT);
	}

	private readonly getTabManagerBenutzerprofil = (): TabManager => {
		return this.createTabManager(this.menuBenutzerprofil, this.menuBenutzerprofilHidden(), this.data.view.name, this.setTab, ViewType.DEFAULT);
	};

	private readonly getTabManagerEinstellungen = (): TabManager => {
		return this.createTabManager(this.menuEinstellungen, this.menuEinstellungenHidden(), this.data.view.name, this.setTab, ViewType.DEFAULT);
	};

	private readonly getTabManagerSchule = (): TabManager => {
		return this.createTabManager(this.menuSchule, this.menuSchuleHidden(), this.data.view.name, this.setTab, ViewType.DEFAULT);
	};

	private readonly getTabManagerNotenmodul = (): TabManager => {
		return this.createTabManager(this.menuNotenmodul, this.menuNotenmodulHidden(), this.data.view.name, this.setTab, ViewType.DEFAULT);
	};

	private readonly setTab = async (value: TabData) => {
		const node = RouteNode.getNodeByName(value.name);
		if (node === undefined) {
			throw new DeveloperNotificationException("Unbekannte Route");
		}
		const routingStatus = await RouteManager.doRoute(node.getRoute());
		if (routingStatus === RoutingStatus.SUCCESS) {
			this.data.setView(node, this.children);
		}
	};

}

export const routeApp = new RouteApp();
